'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { OVERLAYS, type OverlayType } from './filterPresets'

interface OverlayControlsProps {
  /** URL of the image to show in thumbnails */
  imageUrl: string
  /** Currently selected overlay */
  activeOverlay: OverlayType
  /** Called when overlay changes */
  onSelect: (overlay: OverlayType) => void
}

/**
 * OverlayControls
 *
 * Grid of overlay options (vignettes, gradients, etc.)
 */
export function OverlayControls({
  imageUrl,
  activeOverlay,
  onSelect,
}: OverlayControlsProps) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Add a gradient overlay or vignette effect
      </p>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {OVERLAYS.map((overlay) => {
          const isActive = activeOverlay === overlay.id

          return (
            <button
              key={overlay.id}
              onClick={() => onSelect(overlay.id)}
              className={cn(
                'flex flex-col items-center gap-1.5 p-1.5 rounded-lg',
                'transition-all duration-150 touch-manipulation',
                'hover:bg-muted',
                isActive && 'ring-2 ring-primary bg-muted'
              )}
            >
              {/* Preview thumbnail with overlay - optimized with next/image */}
              <div
                className={cn(
                  'relative w-full aspect-square rounded-md overflow-hidden',
                  'bg-muted border border-border'
                )}
              >
                <Image
                  src={imageUrl}
                  alt={overlay.name}
                  fill
                  sizes="80px"
                  className="object-cover"
                  loading="lazy"
                />
                {/* Overlay layer */}
                {overlay.id !== 'none' && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: overlay.css,
                      mixBlendMode:
                        overlay.blendMode as React.CSSProperties['mixBlendMode'],
                      opacity: overlay.opacity,
                    }}
                  />
                )}
              </div>

              {/* Overlay name */}
              <span
                className={cn(
                  'text-xs font-medium truncate w-full text-center',
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                {overlay.name}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
